import {
  IItemBinLocation,
  IBinLocation,
  IBinLocationAttributes,
  PhysicalLocationType,
  LocationType,
  IBinLocationContentSummary,
  IAutocompleteEntry,
  IBinLocationPrintJob,
  ISetCountingInstruction,
  IItem,
  IWarehouse,
  IIBinLocationDao$ICompanion$ISortingCodeUpdate,
  IDefaultItemForLocation,
  IBinLocationStockExpectation,
  ICreateBinLocation,
} from 'interfaces';
import { Registry } from '.';
import { saveBase64UrlEncode } from 'component_utils/utils';
import { IItemMovementHistoryEntry } from 'interfaces';

export default ({ get, post, destroy }: Registry) => {
  return {
    binLocation: {
      findBinLocation: (code: string): Promise<IBinLocation> => get(`/api/v1/bin_location/find/${saveBase64UrlEncode(code)}`),
      findMultipleBinLocations: (codes: string[]): Promise<IBinLocation[]> => post(`/api/v1/bin_location/find`, codes),

      getWarehouses: () => get<IWarehouse[]>('/api/v1/bin_location/warehouses'),

      isDefaultLocationFor: (code: string) => get<IDefaultItemForLocation[]>(`/api/v1/bin_location/${code}/default_location_for`),

      findAutocomplete: (code: string): Promise<IAutocompleteEntry[]> =>
        get(`/api/v1/bin_location/autocomplete/${saveBase64UrlEncode(code)}`),

      getBinLocation: (code: string): Promise<IBinLocation> => get(`/api/v1/bin_location/${code}`),
      getMultipleBinLocations: (codes: string[]): Promise<IBinLocation[]> => post(`/api/v1/bin_location/get`, codes),
      getBinLocationAttributes: (code: string): Promise<IBinLocationAttributes> =>
        get(`/api/v1/bin_location/${code}/attributes`),
      getBinLocationContents: (code: string): Promise<Array<IItemBinLocation>> =>
        get(`/api/v1/bin_location/${code}/contents`),
      getMultipleBinLocationContents: (codes: string[]): Promise<Array<IItemBinLocation>> =>
        post(`/api/v1/bin_location/multiple/contents`, codes),
      getHistory: (code: string): Promise<IItemMovementHistoryEntry[]> => get(`/api/v1/bin_location/${code}/history`),
      getBinLocationsByPhysicalType: (type: PhysicalLocationType): Promise<Array<IBinLocation>> =>
        get(`/api/v1/bin_location/physicalLocationType/${type}`),
      getBinLocationContentSummaryByLocationType: (type: LocationType): Promise<IBinLocationContentSummary[]> =>
        get(`/api/v1/bin_location/locationType/${type}/contentSummary`),
      getBinLocationsByOrderGroup: (orderGroup: string): Promise<Array<IBinLocation>> =>
        orderGroup ? get(`/api/v1/bin_location/preferredOrderGroup/${saveBase64UrlEncode(orderGroup)}`) : Promise.resolve([]),
      setPreferredOrderGroup: (code: string, orderGroup: string, expectedOrderGroup: string): Promise<{ succeeded: boolean, currentOrderGroup: string }> =>
        post(`/api/v1/bin_location/${code}/setPreferredOrderGroup`, { orderGroup, expectedOrderGroup }),
      setCountingState: (code: string, setCountingInstruction: ISetCountingInstruction): Promise<void> =>
        post(`/api/v1/bin_location/${code}/set_counting`, setCountingInstruction),
      getItemsToCount: (code: string): Promise<IItem[]> =>
        get(`/api/v1/bin_location/${code}/counting/items`),
      setReadyForProcessing: (code: string, value: boolean): Promise<void> =>
        post(`/api/v1/bin_location/${code}/readyForProcessing/${value.toString()}`, null),
      setStockMovedToKardex: (code: string, targetBinCode: string): Promise<void> => 
        post(`/api/v1/bin_location/${code}/setStockMovedToKardex`, { targetBinCode }),
      setBlockStockMovements: (code: string, block: boolean, reason: string): Promise<void> => 
        post(`/api/v1/bin_location/${code}/setBlockStockMovements/${block.toString()}`, { reason }),
      setSortingCodes: (updates: IIBinLocationDao$ICompanion$ISortingCodeUpdate[]) => post(
        '/api/v1/bin_location/sorting_code_updates',
        updates
      ),
      setBarcode: (code: string, barcode: string): Promise<void> => post(`/api/v1/bin_location/${code}/barcode`, { barcode }),
      setLocationType: (code: string, type: LocationType): Promise<void> => post(`/api/v1/bin_location/${code}/type`, { type }),
      setPhysicalLocationType: (code: string, type: PhysicalLocationType): Promise<void> => post(`/api/v1/bin_location/${code}/physical_type`, { type }),
      printLabel: (
        printerId: number,
        jobs: IBinLocationPrintJob,
      ): Promise<any> =>
        post(`/api/v1/bin_location/label/${printerId}`, jobs),
      printLargeLabel: (
        printerId: number,
        jobs: IBinLocationPrintJob,
      ): Promise<any> =>
        post(`/api/v1/bin_location/large_label/${printerId}`, jobs),

      createNew: (data: ICreateBinLocation) => 
        post<{binCode: string}>(`/api/v1/bin_location/create_bin_location`, data),

      stockExpectations: {
        getStockExpectations: (binCode: string) => get<IBinLocationStockExpectation[]>(`/api/v1/bin_location/${binCode}/stockExpectations`),
        saveStockExpectations: (binCode: string, expectation: IBinLocationStockExpectation) => post(`/api/v1/bin_location/${binCode}/stockExpectations`, expectation),
        deleteStockExpectations: (binCode: string, itemCode: string) => destroy(`/api/v1/bin_location/${binCode}/stockExpectations/${saveBase64UrlEncode(itemCode)}`),
      },
    },
  };
};
